import React, { useContext, useEffect, useState, forwardRef, useImperativeHandle } from "react"
import { CAMPING_STYLES, ELECTRICS, KINDS } from "../../../constants"
import { PRICE_DURATIONS } from "../../../constants/price_durations"
import { CampProps, PriceProps, SiteProps } from "../../../interfaces"
import { validCampingStyles, validDurations, validKinds } from "../../../types"
import { titleCase } from "../../../utils"
import classNames from "../../../utils/classNames"
import SelectInput, { Option } from "../../ui/SelectInput"
import Toggle from "../../ui/Toggle"
import { AdminContext } from "../../../contexts/AdminContext"
import SitePhotoUpload from "../SitePhotoUpload"
import RichTextInput from "../../ui/RichTextInput"
import InputFieldWithCurrency from "../Inputs/InputFieldWithCurrency"
import Button from "../../ui/Button"
import VendorCalendarModalContent, { ExternalCalendarUrlInput } from "../VendorCalendars/VendorCalendarModalContent"
import IVendorCalendar from "../../../interfaces/IVendorCalendar"
import formatLengthUnit from "~/utils/formatLengthUnit"
import Feature from "~/components/DetailsPayment/Feature"
import { bedFeatures, booleanFeatures, features, lodgingFeatures } from "~/components/Sites/SiteFeatures"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { EditSiteFormSchema, EditSiteFormType } from "~/components/ui/EditSiteForm/EditSiteFormSchema.validation"
import { useModal } from "~/contexts/ModalProvider"
import useSites from "~/api2/hooks/useSites"

const kindOptions = [
  { label: "Back-in", value: KINDS.RV_BACKIN },
  { label: "Pull-through", value: KINDS.RV_PULLTHROUGH },
  { label: "Pull-in", value: KINDS.RV_PULLIN },
  { label: "Hybrid", value: KINDS.RV_HYBRID },
  // NOTE: these are automatically set when changing camping style. Legacy reasons.
  { label: "Tent", value: KINDS.TENT, hidden: true },
  { label: "Cabin", value: KINDS.CABIN, hidden: true },
  { label: "Boat Slip", value: KINDS.BOAT_SLIP, hidden: true },
  { label: "Boat Rental", value: KINDS.BOAT_RENTAL, hidden: true },
  { label: "Mobile Home", value: KINDS.MOBILE_HOME, hidden: true },
  { label: "Storage", value: KINDS.STORAGE, hidden: true },
  { label: "Pavilion", value: KINDS.PAVILION, hidden: true },
  { label: "Cabana", value: KINDS.CABANA, hidden: true },
  { label: "Event Center", value: KINDS.EVENT_CENTER, hidden: true },
  { label: "Garage", value: KINDS.GARAGE, hidden: true },
  { label: "Horse Pen", value: KINDS.HORSE_PEN, hidden: true },
  { label: "Horse Stall", value: KINDS.HORSE_STALL, hidden: true },
  { label: "RV Storage", value: KINDS.RV_STORAGE, hidden: true },
  { label: "Rental RV", value: KINDS.RENTAL_RV, hidden: true },
  { label: "Lodge", value: KINDS.LODGE, hidden: true },
  { label: "Golf Cart", value: KINDS.GOLF_CART, hidden: true },
  { label: "Pool", value: KINDS.POOL, hidden: true },
  { label: "Shelter", value: KINDS.SHELTER, hidden: true },
  { label: "Vendor Booth", value: KINDS.VENDOR_BOOTH, hidden: true },
  { label: "Disc Golf", value: KINDS.DISC_GOLF, hidden: true },
  { label: "Dry Camping", value: KINDS.DRY_CAMPING, hidden: true },
  { label: "Dome", value: KINDS.DOME, hidden: true },
  { label: "Glamping", value: KINDS.GLAMPING, hidden: true },
  { label: "Home", value: KINDS.HOME, hidden: true },
  { label: "Lodging", value: KINDS.LODGING, hidden: true },
  { label: "Bonfire", value: KINDS.BONFIRE, hidden: true },
  { label: "Truck", value: CAMPING_STYLES.TRUCK, hidden: true },
  { label: "Group", value: CAMPING_STYLES.GROUP, hidden: true },
  { label: "Cottage", value: CAMPING_STYLES.COTTAGE, hidden: true },
  { label: "Main House", value: CAMPING_STYLES.MAIN_HOUSE, hidden: true },
  { label: "Tipi", value: CAMPING_STYLES.TIPI, hidden: true },
  { label: "Kayak", value: CAMPING_STYLES.KAYAK, hidden: true },
  { label: "Van", value: CAMPING_STYLES.VAN, hidden: true },
  { label: "Room", value: CAMPING_STYLES.ROOM, hidden: true },
  { label: "Rental Item", value: CAMPING_STYLES.RENTAL_ITEM, hidden: true },
  { label: "Generator", value: CAMPING_STYLES.GENERATOR, hidden: true },
  { label: "Picnic Area", value: CAMPING_STYLES.PICNIC_AREA, hidden: true },
  { label: "Kitchen", value: CAMPING_STYLES.KITCHEN, hidden: true },
  { label: "Platform", value: CAMPING_STYLES.PLATFORM, hidden: true },
  { label: "Canvas Tent", value: CAMPING_STYLES.CANVAS_TENT, hidden: true },
]

const directionKinds = [KINDS.RV_BACKIN, KINDS.RV_PULLTHROUGH, KINDS.RV_PULLIN, KINDS.RV_HYBRID] as const

const electricOptions = [
  { label: "Don't show electric", value: null },
  { label: "Electric (use where amperages don't matter)", value: ELECTRICS.BASIC },
  { label: "No electric", value: ELECTRICS.NONE },
  ...Object.values(ELECTRICS)
    .filter((e) => e && e !== "basic" && e !== "none")
    .map((e) => ({ label: titleCase(e) + " amp", value: e })),
]

const generatePriceTypesFor = (sites: SiteProps[]): { label: string; duration: validDurations }[] => {
  let priceTypes = [
    { label: "Nightly rate", duration: PRICE_DURATIONS.NIGHTLY },
    null,
    { label: "Weekly rate", duration: PRICE_DURATIONS.WEEKLY },
    { label: "Monthly rate", duration: PRICE_DURATIONS.MONTHLY },
    { label: "Seasonal rate", duration: PRICE_DURATIONS.SEASONAL },
    null,
  ] as { label: string; duration: validDurations }[]

  if (!sites || !Array.isArray(sites)) {
    return priceTypes.filter((p) => p)
  }

  if (sites.flatMap((s) => s.prices || []).some((p) => p.duration === PRICE_DURATIONS.NIGHTLY_2_PLUS)) {
    priceTypes[1] = { label: "Nightly rate (2+ nights)", duration: PRICE_DURATIONS.NIGHTLY_2_PLUS }
  }

  if (sites.flatMap((s) => s.prices || []).some((p) => p.duration === PRICE_DURATIONS.FLAT_RATE)) {
    priceTypes[5] = { label: "Flat rate", duration: PRICE_DURATIONS.FLAT_RATE }
  }

  return priceTypes.filter((p) => p)
}

const kindToStyle = (kind: validKinds): validCampingStyles => {
  if (!kind) return

  const rvKinds: validKinds[] = [KINDS.RV_BACKIN, KINDS.RV_PULLTHROUGH, KINDS.RV_HYBRID, KINDS.RV_PULLIN]

  if (rvKinds.includes(kind)) {
    return CAMPING_STYLES.RV
  } else {
    return kind as validCampingStyles
  }
}

interface ComponentProps {
  site?: SiteProps
  camp?: CampProps
  isProcessing: boolean
  errorMessage: string
  setErrorMessage: (message: string) => void
  setIsSiteSaveDisabled: React.Dispatch<React.SetStateAction<boolean>>
  setIsSlideoverOpen: React.Dispatch<React.SetStateAction<boolean>>
  showFeatures: boolean
  setShowFeatures: React.Dispatch<React.SetStateAction<boolean>>
  onNotesChange?: (notes: string | null) => void
  onCheckinPolicyAdditionsChange?: (checkinPolicyAdditions: string | null) => void
}

const EditSiteForm = (props: ComponentProps, ref: React.ForwardedRef<{ handleSubmit: (data: any) => void }>) => {
  const { isParkAdminUser, camp } = useContext(AdminContext)
  const { index: sites } = useSites()
  const { openModal } = useModal()

  // Helper to build all price durations for the form state
  const buildAllPrices = (sites, backendPrices) => {
    const allDurations = generatePriceTypesFor(sites).map(type => type.duration);
    return allDurations.map(duration => {
      const found = backendPrices.find(p => p.duration === duration);
      return found
        ? {
          ...found,
          amount_in_cents: (found.amount_in_cents / 100)?.toString() ?? "",
          isActive: typeof found.isActive === 'boolean' ? found.isActive : true,
        }
        : {
          duration,
          amount_in_cents: "",
          isActive: false,
        };
    });
  };

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors, isDirty },
    reset,
  } = useForm<EditSiteFormType>({
    resolver: zodResolver(EditSiteFormSchema),
    defaultValues: {
      ...props.site,
      camping_style: kindToStyle(props.site?.kind as validKinds),
      max_rig_length: props.site?.max_rig_length?.toString() || null,
      camper_capacity: props.site?.camper_capacity?.toString() || null,
      minimum_capacity: props.site?.minimum_capacity?.toString() || null,
      current_meter_reading: props.site?.last_meter_reading?.toString() || null,
      prices: buildAllPrices(sites?.data, props.site?.prices || []),
      vendor_calendars: props.site?.vendor_calendars || [],
      // Initialize all boolean features
      fire_pit: props.site?.fire_pit || false,
      has_grill_barbecue: props.site?.has_grill_barbecue || false,
      pets: props.site?.pets || false,
      has_wifi: props.site?.has_wifi || false,
      extra_car_space: props.site?.extra_car_space || false,
      premium_view: props.site?.premium_view || false,
      cable_tv: props.site?.cable_tv || false,
      has_towels: props.site?.has_towels || false,
      has_private_bathroom: props.site?.has_private_bathroom || false,
      has_heating: props.site?.has_heating || false,
      has_air_conditioning: props.site?.has_air_conditioning || false,
      has_microwave: props.site?.has_microwave || false,
      has_oven: props.site?.has_oven || false,
      has_stove: props.site?.has_stove || false,
      has_hotplate: props.site?.has_hotplate || false,
      has_refrigerator: props.site?.has_refrigerator || false,
      has_tv: props.site?.has_tv || false,
      has_coffee_machine: props.site?.has_coffee_machine || false,
      is_tentable: props.site?.is_tentable || false,
      has_laundry: props.site?.has_laundry || false,
      public_bathrooms: props.site?.public_bathrooms || false,
      hot_showers: props.site?.hot_showers || false,
      public_kitchen: props.site?.public_kitchen || false,
      has_playground: props.site?.has_playground || false,
      has_propane_fill_up: props.site?.has_propane_fill_up || false,
      has_volleyball_court: props.site?.has_volleyball_court || false,
      has_basketball_court: props.site?.has_basketball_court || false,
      disc_golf_course: props.site?.disc_golf_course || false,
      has_golf_course: props.site?.has_golf_course || false,
      has_mini_golf_course: props.site?.has_mini_golf_course || false,
      has_restaurant: props.site?.has_restaurant || false,
      has_community_fire_pit: props.site?.has_community_fire_pit || false,
      dog_park: props.site?.dog_park || false,
      pool: props.site?.pool || false,
      dump_station: props.site?.dump_station || false,
      dumpster: props.site?.dumpster || false,
      general_store: props.site?.general_store || false,
      picnic_table: props.site?.picnic_table || false,
      sewage: props.site?.sewage || false,
      // Fix validation errors
      badge_label: props.site?.badge_label || "",
      is_available: props.site?.is_available ?? true,
      last_meter_reading: props.site?.last_meter_reading ? Number(props.site.last_meter_reading) : 0,
      laundry_cost: props.site?.laundry_cost ? Number(props.site.laundry_cost) : 0,
      wifi_extra_charge_cents: props.site?.wifi_extra_charge_cents || 0,
      wild_energy_meter_location_id: props.site?.wild_energy_meter_location_id || "",
      // Initialize bed features as numbers
      number_of_single_beds: props.site?.number_of_single_beds || 0,
      number_of_twin_beds: props.site?.number_of_twin_beds || 0,
      number_of_bunk_beds: props.site?.number_of_bunk_beds || 0,
      number_of_futon_beds: props.site?.number_of_futon_beds || 0,
      number_of_full_beds: props.site?.number_of_full_beds || 0,
      number_of_queen_beds: props.site?.number_of_queen_beds || 0,
      number_of_king_beds: props.site?.number_of_king_beds || 0,
    },
  })

  useEffect(() => {
    if (props.site) {
      reset({
        ...props.site,
        camping_style: kindToStyle(props.site?.kind as validKinds),
        max_rig_length: props.site?.max_rig_length?.toString() || null,
        camper_capacity: props.site?.camper_capacity?.toString() || null,
        minimum_capacity: props.site?.minimum_capacity?.toString() || null,
        current_meter_reading: props.site?.last_meter_reading?.toString() || null,
        prices: buildAllPrices(sites?.data, props.site?.prices || []),
        vendor_calendars: props.site?.vendor_calendars || [],
        // Initialize all boolean features
        fire_pit: props.site?.fire_pit || false,
        has_grill_barbecue: props.site?.has_grill_barbecue || false,
        pets: props.site?.pets || false,
        has_wifi: props.site?.has_wifi || false,
        extra_car_space: props.site?.extra_car_space || false,
        premium_view: props.site?.premium_view || false,
        cable_tv: props.site?.cable_tv || false,
        has_towels: props.site?.has_towels || false,
        has_private_bathroom: props.site?.has_private_bathroom || false,
        has_heating: props.site?.has_heating || false,
        has_air_conditioning: props.site?.has_air_conditioning || false,
        has_microwave: props.site?.has_microwave || false,
        has_oven: props.site?.has_oven || false,
        has_stove: props.site?.has_stove || false,
        has_hotplate: props.site?.has_hotplate || false,
        has_refrigerator: props.site?.has_refrigerator || false,
        has_tv: props.site?.has_tv || false,
        has_coffee_machine: props.site?.has_coffee_machine || false,
        is_tentable: props.site?.is_tentable || false,
        has_laundry: props.site?.has_laundry || false,
        public_bathrooms: props.site?.public_bathrooms || false,
        hot_showers: props.site?.hot_showers || false,
        public_kitchen: props.site?.public_kitchen || false,
        has_playground: props.site?.has_playground || false,
        has_propane_fill_up: props.site?.has_propane_fill_up || false,
        has_volleyball_court: props.site?.has_volleyball_court || false,
        has_basketball_court: props.site?.has_basketball_court || false,
        disc_golf_course: props.site?.disc_golf_course || false,
        has_golf_course: props.site?.has_golf_course || false,
        has_mini_golf_course: props.site?.has_mini_golf_course || false,
        has_restaurant: props.site?.has_restaurant || false,
        has_community_fire_pit: props.site?.has_community_fire_pit || false,
        dog_park: props.site?.dog_park || false,
        pool: props.site?.pool || false,
        dump_station: props.site?.dump_station || false,
        dumpster: props.site?.dumpster || false,
        general_store: props.site?.general_store || false,
        picnic_table: props.site?.picnic_table || false,
        sewage: props.site?.sewage || false,
        // Fix validation errors
        badge_label: props.site?.badge_label || "",
        is_available: props.site?.is_available ?? true,
        last_meter_reading: props.site?.last_meter_reading ? Number(props.site.last_meter_reading) : 0,
        laundry_cost: props.site?.laundry_cost ? Number(props.site.laundry_cost) : 0,
        wifi_extra_charge_cents: props.site?.wifi_extra_charge_cents || 0,
        wild_energy_meter_location_id: props.site?.wild_energy_meter_location_id || "",
        // Initialize bed features as numbers
        number_of_single_beds: props.site?.number_of_single_beds || 0,
        number_of_twin_beds: props.site?.number_of_twin_beds || 0,
        number_of_bunk_beds: props.site?.number_of_bunk_beds || 0,
        number_of_futon_beds: props.site?.number_of_futon_beds || 0,
        number_of_full_beds: props.site?.number_of_full_beds || 0,
        number_of_queen_beds: props.site?.number_of_queen_beds || 0,
        number_of_king_beds: props.site?.number_of_king_beds || 0,
      })
    }
  }, [props.site, reset, sites?.data])

  const campingStyle = watch("camping_style")
  const vendorCalendars = watch("vendor_calendars")


  const showCamperCapacity = campingStyle !== CAMPING_STYLES.RV && campingStyle !== CAMPING_STYLES.BOAT_SLIP
  const showMinimumCapacity = campingStyle !== CAMPING_STYLES.RV && campingStyle !== CAMPING_STYLES.BOAT_SLIP

  const rigLengthStyles: validCampingStyles[] = [CAMPING_STYLES.RV, CAMPING_STYLES.BOAT_SLIP]

  const waterOptions = [
    { label: "Yes", value: true },
    { label: "No", value: false },
    { label: "Not specified", value: null },
  ]
  const linenOptions = [
    { label: "Provided", value: true },
    { label: "Not provided", value: false },
    { label: "Not specified", value: null },
  ]

  const handleAddVendorCalendar = (e) => {
    props.setIsSlideoverOpen(false)
    e.preventDefault()

    openModal("generic", {
      children: <VendorCalendarModalContent site={props.site} vendorCalendar={null} onVendorCalendarsChange={(newVendorCalendars) => {
        setValue("vendor_calendars", newVendorCalendars)
      }} />,
      onClose: () => {
        props.setIsSlideoverOpen(true)
      },
    })
  }

  const handleEditVendorCalendar = (vendorCalendar: IVendorCalendar) => {
    props.setIsSlideoverOpen(false)

    openModal("generic", {
      children: <VendorCalendarModalContent site={props.site} vendorCalendar={vendorCalendar} onVendorCalendarsChange={(newVendorCalendars) => {
        setValue("vendor_calendars", newVendorCalendars)
      }} />,
      onClose: () => {
        props.setIsSlideoverOpen(true)
      },
    })
  }

  // Toggle the feature's status
  const toggleFeature = (feature: string) => {
    const currentValue = getValues(feature as any)
    setValue(feature as any, !currentValue)
  }

  // Create a wrapper for the site object that the Feature component expects
  const siteForFeatures = {
    ...props.site,
    ...watch(),
  } as SiteProps

  useImperativeHandle(ref, () => ({
    handleSubmit: (callback: (data: any) => void) => {
      const submitHandler = handleSubmit((data) => {
        callback({ ...data, prices: data.prices })
      }, (errors) => {
        // Form validation failed - log errors for debugging
        props.setErrorMessage('Please fix the form errors before saving.')
      })
      submitHandler()
    },
  }))

  return (
    <div className="pb-2">
      <div>
        <Controller
          name="is_bookable"
          control={control}
          render={({ field }) => (
            <Toggle
              disabled={props.isProcessing}
              checked={field.value}
              onChange={field.onChange}
              name="isBookable"
              label="Bookable"
              description="When disabled, this site will not be bookable online."
              dataTestId="is-bookable"
            />
          )}
        />
      </div>

      <div>
        <label className="block text-md text-gray-900 mt-2">Site name</label>
        <div className="mt-1">
          <input
            type="text"
            {...register("name")}
            disabled={props.isProcessing}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600"
            data-testid="site-name"
          />
          {errors.name && <span className="text-red-500 text-sm mt-1">{errors.name.message}</span>}
        </div>
      </div>

      <div className="flex">
        {(campingStyle === CAMPING_STYLES.RV || (watch("kind") && directionKinds.includes(watch("kind") as "back-in" | "pull-in" | "pull-through" | "hybrid"))) && (
          <div className="flex-1">
            <label className="block text-md text-gray-900 mt-2">Direction </label>
            <div className="mt-1">
              <SelectInput
                name="kind"
                options={kindOptions}
                selected={kindOptions.find((o) => o.value === watch("kind"))}
                setSelected={(o: Option) => setValue("kind", o.value)}
                dataTestId="site-kind"
              />
              {errors.kind && <span className="text-red-500 text-sm mt-1">{errors.kind.message}</span>}
            </div>
          </div>
        )}
      </div>

      {rigLengthStyles.includes(campingStyle as validCampingStyles) && (
        <div>
          <label className="block text-md text-gray-900 mt-2">
            Max {campingStyle === CAMPING_STYLES.BOAT_SLIP ? "boat" : "rig"} length (
            {formatLengthUnit(camp.length_unit)}){" "}
          </label>
          <div className="mt-1">
            <input
              type="number"
              step="1"
              {...register("max_rig_length")}
              disabled={props.isProcessing}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600"
              data-testid="max-rig-length"
            />
            {errors.max_rig_length && <span className="text-red-500 text-sm mt-1">{errors.max_rig_length.message}</span>}
          </div>
        </div>
      )}

      <div>
        <label className="block text-md text-gray-900 mt-2">Electric (amps) </label>
        <div className="mt-1">
          <SelectInput
            name="electric"
            options={electricOptions}
            selected={electricOptions.find((o) => o.value === watch("electric"))}
            setSelected={(o: Option) => setValue("electric", o.value)}
          />
          {errors.electric && <span className="text-red-500 text-sm mt-1">{errors.electric.message}</span>}
        </div>
      </div>

      {showCamperCapacity && (
        <div>
          <label className="block text-md text-gray-900 mt-2">Maximum capacity</label>
          <div className="mt-1">
            <input
              type="number"
              step="1"
              {...register("camper_capacity")}
              disabled={props.isProcessing}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600"
              data-testid="max-capacity"
            />
            {errors.camper_capacity && <span className="text-red-500 text-sm mt-1">{errors.camper_capacity.message}</span>}
          </div>
        </div>
      )}

      {showMinimumCapacity && isParkAdminUser && (
        <div>
          <label className="block text-md text-gray-900 mt-2">Minimum capacity</label>
          <div className="mt-1">
            <input
              type="number"
              step="1"
              {...register("minimum_capacity")}
              disabled={props.isProcessing}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600"
              data-testid="minimum-capacity"
            />
            {errors.minimum_capacity && <span className="text-red-500 text-sm mt-1">{errors.minimum_capacity.message}</span>}
          </div>
        </div>
      )}

      {(campingStyle == CAMPING_STYLES.RV || campingStyle == CAMPING_STYLES.BOAT_SLIP) && (
        <div>
          <label className="block text-md text-gray-900 mt-2">Current Meter Reading (KWh) </label>
          <div className="mt-1">
            <input
              type="number"
              step="1"
              {...register("current_meter_reading")}
              disabled={props.isProcessing}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600"
              data-testid="current-meter-reading"
            />
            {errors.current_meter_reading && <span className="text-red-500 text-sm mt-1">{errors.current_meter_reading.message}</span>}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="prices" className="block font-semibold text-gray-900 pt-4 mb-4">
          Prices{" "}
        </label>
        <div className="flex">
          <label htmlFor="project-name" className="w-1/5 mr-4 text-md text-gray-900">
            {" "}
            Enabled{" "}
          </label>
          <label htmlFor="project-name" className="w-2/5 mr-4 text-md text-gray-900">
            {" "}
            Rate Type{" "}
          </label>
          <label htmlFor="project-name" className="w-2/5 text-md text-gray-900">
            {" "}
            Amount{" "}
          </label>
        </div>

        {generatePriceTypesFor(sites?.data)
          .map((type) => {
            const prices = watch("prices")
            const activePrices = prices?.filter(p => !!p.isActive) || []
            const isOnlyOneActive = activePrices.length === 1
            const price = basePriceFor(type.duration, prices as any[])
            const isPersisted = !!price && !!price.id
            const isActive = !!price && typeof price.isActive === 'boolean' ? price.isActive : isPersisted
            const priceIndex = prices.findIndex(p => p.duration === type.duration)
            const toggleDisabled = isActive && isOnlyOneActive && activePrices.length === 1;

            return (
              <div className="flex flex-row items-center mb-2" key={type.duration}>
                <div className="w-1/5 mr-4">
                  <Toggle
                    dataTestId="price-toggle"
                    disabled={toggleDisabled || props.isProcessing}
                    name={`prices[${type.duration}][isActive]`}
                    checked={isActive}
                    onChange={(v) => {
                      const updatedPrices = [...prices]
                      if (priceIndex >= 0) {
                        updatedPrices[priceIndex] = { ...updatedPrices[priceIndex], isActive: v }
                        setValue("prices", updatedPrices)
                      }
                    }}
                  />
                </div>
                <div className={classNames("w-2/5 mr-4")}>{type.label}</div>
                <div className={classNames("w-2/5")}> {/* Always editable */}
                  <div className="mt-1">
                    <InputFieldWithCurrency
                      dataTestId="price-amount"
                      type="number"
                      disabled={props.isProcessing}
                      value={watch(`prices.${priceIndex}.amount_in_cents`)}
                      name={`prices.${priceIndex}.amount_in_cents`}
                      id="amount"
                      currency={props.camp.currency}
                      full
                      register={register}
                      step="1"
                      onChange={(e) => {
                        const updatedPrices = [...prices]
                        if (priceIndex >= 0) {
                          updatedPrices[priceIndex] = { ...updatedPrices[priceIndex], amount_in_cents: e.target.value }
                          setValue("prices", updatedPrices)
                        }
                      }}
                    />
                    {errors.prices?.[priceIndex]?.amount_in_cents && (
                      <span className="text-red-500 text-sm mt-1">{errors.prices[priceIndex].amount_in_cents.message}</span>
                    )}
                    <input
                      data-testid="price-duration"
                      type="text"
                      readOnly
                      {...register(`prices.${priceIndex}.duration`)}
                      defaultValue={type.duration}
                      className="hidden"
                    />
                    {price && (
                      <input
                        data-testid="price-id"
                        type="text"
                        readOnly
                        {...register(`prices.${priceIndex}.id`)}
                        defaultValue={price.id}
                        className="hidden"
                      />
                    )}
                  </div>
                </div>
              </div>
            )
          })
          .filter((c) => c)}

        {errors.prices && <span className="text-red-500 text-sm mt-1">{errors.prices.message}</span>}

        <div>
          <label className="block text-md text-gray-900">
            Site Notes <span className="text-gray-400">(shown when booking)</span>
          </label>
          <div className="mt-1">
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <RichTextInput
                  dataTestId="site-notes"
                  {...field}
                  setValue={(value: string) => {
                    field.onChange(value)
                    setValue("notes", value, { shouldValidate: true, shouldDirty: true })
                  }}
                  isDisabled={props.isProcessing}
                  id="siteNotes"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600"
                  hyperlink
                />
              )}
            />
            {errors.notes && <span className="text-red-500 text-sm mt-1">{errors.notes.message}</span>}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-md text-gray-900">
            Site-specific check-in policy <span className="text-gray-400">(shown on confirmation page)</span>
          </label>
          <div className="mt-1">
            <Controller
              name="checkin_policy_additions"
              control={control}
              render={({ field }) => (
                <RichTextInput
                  dataTestId="site-checkin-policy-additions"
                  {...field}
                  setValue={(value: string) => {
                    field.onChange(value)
                    setValue("checkin_policy_additions", value, { shouldValidate: true, shouldDirty: true })
                  }}
                  isDisabled={props.isProcessing}
                  id="siteCheckinPolicyAdditions"
                  className="block w-full rounded-md border-gray-700 focus:ring-green-700"
                  hyperlink
                />
              )}
            />
            {errors.checkin_policy_additions && <span className="text-red-500 text-sm mt-1">{errors.checkin_policy_additions.message}</span>}
          </div>
        </div>
        <div className="mt-4">
          <SitePhotoUpload site={props.site} />
        </div>
        <div className="mt-4 mb-4">
          <label className="block font-semibold text-gray-900 pt-4 mb-1">Calendar sync</label>
          <div className="text-sm text-gray-500 mb-4">
            Sync your calendar with another booking platform to avoid double bookings.
          </div>
          <div>
            <Button
              variant="white"
              className="w-full"
              onClick={handleAddVendorCalendar}
              dataTestId="add-external-calendar"
            >
              Add External Calendar Sync
            </Button>
          </div>
          <label className="block font-semibold text-gray-900 pt-4 mb-1">Links</label>
          {vendorCalendars?.length ? (
            <div className="flex flex-col gap-y-5">
              {vendorCalendars.map((vendor) => (
                <div key={vendor.id} className="w-full flex">
                  <ExternalCalendarUrlInput url={vendor.calendar_url} setUrl={() => { }} readOnly />
                  <Button
                    className="ml-2"
                    variant="white"
                    onClick={() => handleEditVendorCalendar(vendor as IVendorCalendar)}
                    dataTestId="edit-external-calendar"
                  >
                    Edit
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-300 italic">No external calendars linked.</div>
          )}
        </div>

        <div>
          <label htmlFor="features" className="block font-semibold text-gray-900 pt-4 mb-4">
            Features{" "}
          </label>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between text-sm">
            <div className="flex gap-2 flex-col flex-1">
              <div className="flex items-center justify-between">
                <Feature name={"water"} value={"water"} site={siteForFeatures} isShowingLabel={true} />
                <SelectInput
                  disabled={props.isProcessing}
                  selected={waterOptions.find((o) => o.value === watch("water"))}
                  setSelected={(option: Option) => {
                    setValue("water", option.value);
                  }}
                  options={waterOptions}
                  className="w-40"
                  dataTestId="water"
                />
              </div>
            </div>

            <div className="flex gap-2 flex-col flex-1 sm:ml-4 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Feature name={"has_linens"} value={"has_linens"} site={siteForFeatures} isShowingLabel={false} />
                  <span className="pl-4">Linens</span>
                </div>
                <SelectInput
                  disabled={props.isProcessing}
                  selected={linenOptions.find((o) => o.value === watch("has_linens"))}
                  setSelected={(option: Option) => {
                    setValue("has_linens", option.value);
                  }}
                  options={linenOptions}
                  className="w-40"
                  dataTestId="has_linens"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4">
            {booleanFeatures.map((feature) => {
              const isActive = Boolean(getValues(feature as any))
              return (
                <React.Fragment key={feature}>
                  <button
                    type="button"
                    onClick={() => toggleFeature(feature)}
                    className={`p-2 rounded-md text-sm ${isActive ? "bg-gray-100 border-gray-500 border" : "bg-white border-gray-100 border"
                      }`}
                  >
                    <Feature name={feature as keyof SiteProps} value={feature} site={siteForFeatures} isShowingLabel={true} />
                  </button>
                  <input hidden readOnly type="text" value={getValues(feature as any) ? "true" : "false"} name={feature} id={feature} />
                </React.Fragment>
              )
            })}
          </div>

          <label htmlFor="lodging-features" className="block font-semibold text-gray-900 mt-6">
            Lodging Features{" "}
          </label>
          <div className="grid grid-cols-2 gap-4 pt-4">
            {lodgingFeatures.map((feature) => {
              const isActive = Boolean(getValues(feature as any))
              return (
                <React.Fragment key={feature}>
                  <button
                    type="button"
                    onClick={() => toggleFeature(feature)}
                    className={`p-2 rounded-md text-sm ${isActive ? "bg-gray-100 border-gray-500 border" : "bg-white border-gray-100 border"
                      }`}
                  >
                    <Feature name={feature as keyof SiteProps} value={feature} site={siteForFeatures} isShowingLabel={true} />
                  </button>
                  <input hidden readOnly type="text" value={getValues(feature as any) ? "true" : "false"} name={feature} id={feature} />
                </React.Fragment>
              )
            })}
          </div>

          <label htmlFor="beds" className="block font-semibold text-gray-900 mt-6">
            Beds{" "}
          </label>
          <div className="grid grid-cols-2 gap-4 pt-4 text-sm">
            {bedFeatures.map((feature) => {
              return (
                <div key={feature} className="flex items-center">
                  <Feature name={feature as keyof SiteProps} value={feature} site={siteForFeatures} isShowingLabel={false} />
                  <span className="flex-1 pl-4">{titleCase(feature.replace("number_of_", ""))}</span>
                  <input
                    type="number"
                    value={String(getValues(feature as any) || 0)}
                    onChange={(e) => setValue(feature as any, Number(e.target.value))}
                    name={feature}
                    id={feature}
                    min="0"
                    className="ml-2 w-14 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm p-2 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const basePriceFor = (duration: validDurations, prices: PriceProps[]): PriceProps => {
  const pricesForDuration = prices.filter((p) => p.duration === duration)
  return pricesForDuration.reduce((acc, price) => {
    if (!price.electric && acc.electric) return price
    if (!price.range_start && acc.range_start) return price
    return acc
  }, pricesForDuration[0])
}

export default forwardRef(EditSiteForm)
